import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";


interface CreateMemberDialogProps {
  projectId: string;
  onClose: () => void;
}

export function CreateMemberDialog({ projectId, onClose }: CreateMemberDialogProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  // Always create as manager - they only get access to this specific project
  const queryClient = useQueryClient();

  const createMemberMutation = useMutation({
    mutationFn: async (data: { projectId: string; email: string; password: string; role: string; fullName?: string }) => {
      const { data: result, error } = await supabase.functions.invoke('create-team-member', {
        body: data
      });
      
      if (error) throw error;
      return result;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Team member created successfully');
      queryClient.invalidateQueries({ queryKey: ['team-project-members', projectId] });
      handleClose();
    },
    onError: (error: any) => {
      console.error('Create member error:', error);
      toast.error(error.message || 'Failed to create team member');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!password.trim()) {
      toast.error('Please enter a password');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    createMemberMutation.mutate({
      projectId,
      email: email.trim(),
      password: password.trim(),
      role: 'manager', // Always create as manager for this project only
      fullName: fullName.trim() || undefined
    });
  };

  const handleClose = () => {
    setEmail("");
    setPassword("");
    setFullName("");
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Project Manager</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Create a new manager account for this project. They will have full access to this project only.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              minLength={6}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name (Optional)</Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="User's full name"
            />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <div className="p-2 bg-muted rounded-md text-sm">
              <strong>Manager</strong> - Full management access to this project only
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMemberMutation.isPending}>
              {createMemberMutation.isPending ? 'Creating...' : 'Create Manager'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}